import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Integration from "@/model/interface/Integration";
import {
  useGetMembersQuery,
  useGetSalesforceUsersQuery,
  useSetSalesforceUserMappingMutation,
  useUpdateIntegrationSettingsMutation,
} from "@/store/rest_api";
import toast from "react-hot-toast";

interface Props {
  integration: Integration;
}

export default function SalesforceUserMapping({ integration }: Props) {
  const isActive = integration.status?.toLowerCase() === "active";
  const { data: members } = useGetMembersQuery({ status: "ACTIVE" });
  const { data: sfUsers, isLoading: sfUsersLoading } = useGetSalesforceUsersQuery(undefined, {
    skip: !isActive,
  });
  const [setMapping] = useSetSalesforceUserMappingMutation();
  const [updateSettings] = useUpdateIntegrationSettingsMutation();
  const defaultOwnerId = integration.settings?.defaultSalesforceOwnerId as string | undefined;

  const handleMapping = async (membershipId: string, salesforceUserId: string | null) => {
    try {
      await setMapping({ membershipId, salesforceUserId }).unwrap();
      toast.success("User mapping updated");
    } catch {
      toast.error("Failed to update user mapping");
    }
  };

  if (!isActive) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Connect your Salesforce account first to configure user mapping.
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <Card>
      <CardHeader>
        <CardTitle>User Mapping</CardTitle>
        <CardDescription>
          Link team members to their Salesforce user accounts. Calls and messages will be attributed
          to the mapped Salesforce user.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-xs text-muted-foreground font-medium">
            <span>Team Member</span>
            <span>Salesforce User</span>
          </div>

          {members?.map((member) => (
            <div key={member.id} className="grid grid-cols-2 gap-4 items-center">
              <div>
                <p className="text-sm font-medium">
                  {member.user?.firstName} {member.user?.lastName}
                </p>
                <p className="text-xs text-muted-foreground">{member.user?.email}</p>
              </div>
              <Select
                value={(member as any).salesforceUserId || "none"}
                onValueChange={(value) =>
                  handleMapping(member.id, value === "none" ? null : value)
                }
                disabled={sfUsersLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Not mapped" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not mapped</SelectItem>
                  {sfUsers?.map((sfUser) => (
                    <SelectItem key={sfUser.id} value={sfUser.id}>
                      {sfUser.name} ({sfUser.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}

          {(!members || members.length === 0) && (
            <p className="text-sm text-muted-foreground text-center py-4">No team members found</p>
          )}
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle>Default Call Owner</CardTitle>
        <CardDescription>
          Fallback Salesforce user for activity attribution when the agent hasn't been mapped above
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <Label>Default owner</Label>
          <Select
            value={defaultOwnerId || "none"}
            onValueChange={async (value) => {
              try {
                await updateSettings({
                  id: integration.id,
                  settings: { defaultSalesforceOwnerId: value === "none" ? null : value },
                }).unwrap();
              } catch {
                toast.error("Failed to update default owner");
              }
            }}
            disabled={sfUsersLoading}
          >
            <SelectTrigger className="w-72">
              <SelectValue placeholder="No default (uses API user)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No default (uses API user)</SelectItem>
              {sfUsers?.map((sfUser) => (
                <SelectItem key={sfUser.id} value={sfUser.id}>
                  {sfUser.name} ({sfUser.email})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
    </>
  );
}
